import React, { useState, useEffect, useCallback } from 'react';
import { extractColors } from 'extract-colors';
import AlbumDetails from './AlbumDetails';

function hexToHSL(hex) {
  let r = parseInt(hex.slice(1, 3), 16) / 255;
  let g = parseInt(hex.slice(3, 5), 16) / 255;
  let b = parseInt(hex.slice(5, 7), 16) / 255;

  let max = Math.max(r, g, b);
  let min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;

  if (max === min) {
    h = s = 0;
  } else {
    let d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
      default: h = 0; break;
    }
    h /= 6;
  }

  return { h: h * 360, s: s * 100, l: l * 100 };
}

function hslToHex(h, s, l) {
  l /= 100;
  const a = s * Math.min(l, 1 - l) / 100;
  const f = n => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

function adjustColorLightness(hex) {
  const hsl = hexToHSL(hex);
  const targetLightness = Math.min(45, 35 + (hsl.s * 0.1));
  return hslToHex(hsl.h, Math.min(100, hsl.s * 1.2), targetLightness);
}

const AlbumView = ({ album, wikiContent }) => {
  const [accentColor, setAccentColor] = useState(null);

  const getSafeImageSrc = (url) => {
    const trustedDomains = ['i.scdn.co'];
    try {
      const urlObj = new URL(url);
      const isTrusted = trustedDomains.some(domain => 
        urlObj.hostname === domain || urlObj.hostname.endsWith('.' + domain)
      );
      return isTrusted ? url : '/logo192.png';
    } catch {
      return '/logo192.png';
    }
  };

  const coverImageUrl = album?.images?.[0]?.url;

  const extractAccentColor = useCallback(async (imageUrl) => {
    try {
      const colors = await extractColors(imageUrl, {
        pixels: 20000,
        distance: 0.15,         // Slightly increased for more color variety
        saturationDistance: 0.15, // More sensitive to saturation differences
        lightnessDistance: 0.15,  // More sensitive to lightness differences
        hueDistance: 0.083333333,
        colorValidator: (r, g, b, a) => {
          if (a < 125) return false;
          
          const maxVal = Math.max(r, g, b);
          const minVal = Math.min(r, g, b);
          const l = (maxVal + minVal) / 510;  // Normalized to 0-1
          const s = maxVal === minVal ? 0 : 
                   (maxVal - minVal) / (l > 0.5 ? (510 - maxVal - minVal) : (maxVal + minVal));
          
          // Enhanced vibrancy requirements
          const minSaturation = 0.3;  // Require at least 30% saturation
          const idealLightness = 0.45; // Target 45% lightness
          const lightnessRange = 0.25; // Accept colors within ±25% of ideal
          
          return s > minSaturation && 
                 Math.abs(l - idealLightness) < lightnessRange &&
                 l > 0.15; // Ensure minimum brightness for visibility
        },
      });

      if (colors.length > 0) {
        // Score colors based on vibrancy and area
        const scoredColors = colors.map(color => {
          const hsl = hexToHSL(color.hex);
          
          // Favor colors with high saturation and moderate lightness
          const saturationScore = hsl.s / 100;
          const lightnessScore = 1 - Math.abs(45 - hsl.l) / 45;
          const areaScore = color.area / 100;
          
          return {
            ...color,
            score: (saturationScore * 1.5) + (lightnessScore * 0.8) + (areaScore * 0.7)
          };
        });

        const sortedColors = scoredColors.sort((a, b) => b.score - a.score);
        const adjustedColor = adjustColorLightness(sortedColors[0].hex);
        setAccentColor(adjustedColor);
      } else {
        setAccentColor('#4a5ab9'); // Vibrant blue-purple fallback
      }
    } catch (error) {
      console.error('Error extracting colors:', error);
      setAccentColor('#4a5ab9');
    }
  }, []);

  useEffect(() => {
    if (coverImageUrl) {
      extractAccentColor(coverImageUrl);
    }
  }, [coverImageUrl, extractAccentColor]);

  return (
    <div className="flex h-full md:flex-row flex-col flex-1 md:overflow-y-auto text-lg">
      <div id="album"
        style={{ backgroundColor: accentColor }}
        className="
          relative grid h-full lg:w-1/2 md:w-1/3 w-full min-[320px]:min-h-80
          md:grid-rows-[minmax(100px,_auto)] md:grid-cols-[1fr]
          grid-rows-[minmax(100px,_auto)] grid-cols-[1fr]
        ">
        <div id="cover" className="
          z-10 justify-items-center
          md:row-start-1 md:row-end-2 md:col-start-1 md:col-end-2
          row-start-1 row-end-2 col-start-1 col-end-2
        ">
          <img
            className="md:w-[548px] w-full max-w-full h-full xl:object-center md:object-top object-center object-contain aspect-square"
            src={getSafeImageSrc(album.images[0].url)}
            alt={album.name}
            loading="lazy"
          />
        </div>
        
        <div id="bgGradient" className="
          bg-gradient-to-tl from-[#FFFBEC] to-black
          z-[1] mix-blend-overlay opacity-30
          row-start-1 col-start-1 col-end-2
        "/>

        <div
          style={{backgroundImage: `url('${getSafeImageSrc(album.images[0].url)}')`}}
          className="
            xl:bg-[length:548px_548px] bg-contain opacity-50 xl:bg-center md:bg-top bg-center
            row-start-1 col-start-1 col-end-2
          "
        />

      </div>

      <AlbumDetails
        album={album}
        accentColor={accentColor}
        wikiContent={wikiContent}
      />
    </div>
  );
};

export default AlbumView;